import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { format, addMinutes, isPast } from "date-fns";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getBookingSlotInfo } from "@/lib/bookingSlotInfo";
import type { Tables } from "@/integrations/supabase/types";
import type {
  ScheduleBooking,
  ScheduleTrainingSession,
  BookingCategory,
} from "./types";
import { CATEGORY_STYLES } from "./types";

interface ScheduleGridCellProps {
  courtId: string;
  courtIdx: number;
  slot: Date;
  slotIdx: number;
  bookings: ScheduleBooking[];
  selected: boolean;
  hall: Tables<"halls"> | null;
  catByUser: Map<string, { slug: string; name: string } | null>;
  getBookingCategory: (booking: ScheduleBooking) => BookingCategory;
  getPlayerName: (booking: ScheduleBooking) => string;
  // activity-click-fix -- when the booking is activity-linked, the cell
  // renders the activity_title + organizer first name instead of the
  // generic player/price label. null = not activity-linked.
  getActivityLabel?: (
    booking: ScheduleBooking,
  ) => { title: string; organizerFirstName: string } | null;
  // schedule-grid-gap-1-fix -- when the booking is league-match-linked
  // (PR #249 backfill), the cell renders "<home> vs <away>" + league
  // name with purple Seriespel styling and routes click to
  // MatchDetailModal (via routeBookingClick's league branch in
  // AdminScheduleGrid). null = not league-match-linked.
  getLeagueMatchLabel?: (
    booking: ScheduleBooking,
  ) => { teams: string; leagueName: string } | null;
  getTrainingSlotInfo: (
    courtId: string,
    slotStart: Date,
  ) => { training: ScheduleTrainingSession; isFirst: boolean; slotCount: number } | null;
  // PR-D Layer 1 (TASK-75) — league_match span info for the cell.
  getLeagueMatchSlotInfo: (
    courtId: string,
    slotStart: Date,
  ) => { match: { id: string; scheduled_time: string; leagues: { match_duration_minutes: number | null; name: string } | null }; isFirst: boolean; slotCount: number } | null;
  // Tidsbokning Phase A — click on a Seriespel overlay opens MatchDetailModal.
  // Optional so existing call sites that don't need match-click stay valid.
  onMatchClick?: (matchId: string) => void;
  // Tidsbokning Phase D — drag the Seriespel block. AdminScheduleGrid wires
  // a pointer-event handler that mirrors the booking-drag pattern.
  onMatchDragStart?: (matchId: string, courtIdx: number, slotIdx: number, slotCount: number, durationMinutes: number, e: React.PointerEvent) => void;
  matchDrag?: {
    matchId: string;
    originCourtIdx: number;
    originSlotIdx: number;
    targetCourtIdx: number;
    targetSlotIdx: number;
    slotCount: number;
    durationMinutes: number;
    dropValid: boolean | null;
  } | null;
  onBookingClick: (booking: ScheduleBooking) => void;
  onDragStart: (courtIdx: number, slotIdx: number) => void;
  onDragEnter: (courtIdx: number, slotIdx: number) => void;
  onSlotClick: (courtIdx: number, slotIdx: number) => void;
  dragRef: React.MutableRefObject<{
    active: boolean;
    anchorCourtIdx: number;
    anchorSlotIdx: number;
    mode: "select" | "deselect";
  } | null>;
  // UPPDRAG-31: extended with `dropValid` so cells can paint the
  // proposed drop range green (valid) / red (invalid) during drag.
  bookingDrag: {
    booking: ScheduleBooking;
    originCourtIdx: number;
    originSlotIdx: number;
    targetCourtIdx: number;
    targetSlotIdx: number;
    slotCount: number;
    dropValid: boolean | null;
  } | null;
  onBookingDragStart: (booking: ScheduleBooking, courtIdx: number, slotIdx: number, slotCount: number, e: React.PointerEvent) => void;
  getRecurringBlockAt: (courtId: string, slotStart: Date) => any;
  getRecurringBookingAt: (courtId: string, slotStart: Date) => any;
  getSubscriptionReleaseAt: (courtId: string, slotStart: Date) => any;
  rbCustomerMap: Map<string, string>;
  onRecurringBlockClick: (block: any) => void;
  onSubscriptionClick: (sub: any) => void;
  // PR-AK — Multi-select bulk-cancel state.
  multiSelectMode?: boolean;
  bulkSelectedBookingIds?: Set<string>;
  // Fas 2 click-to-move UX-cleanup — when move-mode is active (i.e. admin
  // pressed "Flytta tid" in BookingDetailPanel), empty-cell mousedown must
  // NOT trigger onDragStart. Otherwise the selection-rect created by
  // handleDragStart is visible alongside the move-mode banner and misleads
  // the operator into thinking click-to-move is broken. The cell's onClick
  // still fires → onSlotClick → handleSlotClick move-branch executes
  // normally (Fas 0.5 empirical verification).
  clickToMoveBooking: ScheduleBooking | null;
}

export function ScheduleGridCell({
  courtId,
  courtIdx,
  slot,
  slotIdx,
  bookings,
  selected,
  hall,
  catByUser,
  getBookingCategory,
  getPlayerName,
  getActivityLabel,
  getLeagueMatchLabel,
  getTrainingSlotInfo,
  getLeagueMatchSlotInfo,
  onMatchClick,
  onMatchDragStart,
  matchDrag,
  onBookingClick,
  onDragStart,
  onDragEnter,
  onSlotClick,
  dragRef,
  bookingDrag,
  onBookingDragStart,
  getRecurringBlockAt,
  getRecurringBookingAt,
  getSubscriptionReleaseAt,
  rbCustomerMap,
  onRecurringBlockClick,
  onSubscriptionClick,
  multiSelectMode,
  bulkSelectedBookingIds,
  clickToMoveBooking,
}: ScheduleGridCellProps) {
  const { t } = useTranslation("admin");
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const info = getBookingSlotInfo(courtId, slot, bookings);
  const gridCol = slotIdx + 2;

  if (info.type === "booking_mid") return null;

  if (info.type === "booking_start") {
    const bStart = new Date(info.booking.start_time);
    const bEnd = new Date(info.booking.end_time);
    const bookingTyped = info.booking as ScheduleBooking;
    const checkedIn = !!bookingTyped.checked_in_at;
    const hallTyped = hall as Tables<"halls"> | null;
    const closesMin = hallTyped?.checkin_closes_minutes ?? 15;
    const checkinEnabled = (hallTyped as any)?.checkin_enabled === true;
    const isMatchiImported = Boolean((bookingTyped as any).matchi_slot_id);
    const noShow = checkinEnabled && !isMatchiImported && !checkedIn && isPast(addMinutes(bStart, closesMin));
    const category = getBookingCategory(info.booking as ScheduleBooking);
    const catStyle = CATEGORY_STYLES[category];
    const playerName = getPlayerName(info.booking as ScheduleBooking);
    // activity-click-fix — when the booking is activity-linked, prefer the
    // activity title for line 1 + organizer first name for line 2.
    const activityLabel = getActivityLabel
      ? getActivityLabel(info.booking as ScheduleBooking)
      : null;
    // schedule-grid-gap-1-fix — when the booking is league-match-linked
    // (PR #249 backfill), render "<home> vs <away>" + league name with
    // purple Seriespel styling (same lila as the overlay branch line 302+
    // — kept as defensive fallback per Q-EX4 for matches without bookings).
    const leagueMatchLabel = getLeagueMatchLabel
      ? getLeagueMatchLabel(info.booking as ScheduleBooking)
      : null;
    const courtName = info.booking.courts?.name ?? "";
    const phone = (info.booking as ScheduleBooking).profiles?.phone ?? "–";
    const price =
      info.booking.price_total != null ? `${info.booking.price_total} kr` : "–";
    const pinCode = info.booking.pin_code ?? "–";

    const isDragOrigin = bookingDrag?.booking.id === info.booking.id;
    const isConfirmedBooking = info.booking.status === "confirmed";
    // PR-AK — Multi-select state for this cell.
    const isBulkSelected = !!multiSelectMode && !!bulkSelectedBookingIds?.has(info.booking.id);
    const dragEnabled = isConfirmedBooking && category !== "blocked" && !multiSelectMode;

    return (
      <TooltipProvider key={slot.toISOString()} delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => { if (!bookingDrag) onBookingClick(info.booking as ScheduleBooking); }}
              onPointerDown={dragEnabled ? (e) => onBookingDragStart(info.booking as ScheduleBooking, courtIdx, slotIdx, info.slotCount, e) : undefined}
              onTouchStart={dragEnabled ? (e) => {
                longPressRef.current = setTimeout(() => {
                  const touch = e.touches[0];
                  if (touch) {
                    onBookingDragStart(info.booking as ScheduleBooking, courtIdx, slotIdx, info.slotCount, { clientX: touch.clientX, clientY: touch.clientY, preventDefault: () => {}, stopPropagation: () => {} } as any);
                  }
                }, 500);
              } : undefined}
              onTouchEnd={() => { if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; } }}
              onTouchMove={() => { if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; } }}
              onMouseDown={category === "blocked" && !multiSelectMode ? (e) => { e.preventDefault(); onDragStart(courtIdx, slotIdx); } : undefined}
              onMouseEnter={category === "blocked" && !multiSelectMode ? () => onDragEnter(courtIdx, slotIdx) : undefined}
              className={cn(
                "rounded-sm overflow-hidden z-10 flex flex-col justify-center px-1.5 border transition-colors min-w-0 h-full",
                dragEnabled ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
                isDragOrigin ? "opacity-30" : "",
                // schedule-grid-gap-1-fix — purple Seriespel styling takes
                // precedence over the booking category background when the
                // booking is league-match-linked. Mirrors the overlay
                // branch (line 363) for visual continuity.
                noShow
                  ? "bg-red-200/80 border-red-400 hover:bg-red-300/80"
                  : leagueMatchLabel
                    ? "bg-purple-200/40 dark:bg-purple-900/20 border-purple-400/60 dark:border-purple-700/60 hover:opacity-80"
                    : cn(catStyle.bg, catStyle.border, "hover:opacity-80"),
                // PR-AJ Layer D: visible ring when this slot has overlapping bookings
                // (e.g. confirmed + blocked at same time). Helps admin spot historical
                // overlap that the new DB trigger now prevents from being created.
                info.conflictBookings.length > 0 && "ring-2 ring-orange-500 ring-offset-1",
                // PR-AK — Bulk-select highlight ring + checkmark overlay.
                isBulkSelected && "ring-2 ring-primary ring-offset-1 brightness-110",
              )}
              style={{ gridColumn: `${gridCol} / span ${info.slotCount}` }}
            >
              <div className="truncate text-[10px] leading-tight text-left min-w-0 w-full">
                {checkedIn && (
                  <CheckCircle2 className="h-3 w-3 text-green-700 shrink-0 inline mr-0.5" />
                )}
                {noShow && (
                  <AlertTriangle className="h-3 w-3 text-red-700 shrink-0 inline mr-0.5" />
                )}
                {/* PR-AJ Layer D: conflict count badge */}
                {info.conflictBookings.length > 0 && (
                  <span className="bg-orange-500 text-white text-[8px] font-bold px-1 rounded mr-0.5">
                    +{info.conflictBookings.length}
                  </span>
                )}
                {/* PR-AK — Bulk-select checkmark badge */}
                {isBulkSelected && (
                  <span className="bg-primary text-primary-foreground text-[9px] font-bold px-1 rounded mr-0.5">✓</span>
                )}
                <span className="font-semibold">
                  {leagueMatchLabel
                    ? leagueMatchLabel.teams
                    : activityLabel
                      ? activityLabel.title
                      : playerName}
                </span>
              </div>
              <div className="truncate text-[9px] leading-tight text-left opacity-70 min-w-0 w-full">
                {leagueMatchLabel ? (
                  // schedule-grid-gap-1-fix — show league name instead of
                  // price for league-match-linked bookings (price_total=0
                  // on the backfilled blocker is meaningless).
                  <span>{leagueMatchLabel.leagueName}</span>
                ) : activityLabel ? (
                  // activity-click-fix — show organizer first name instead
                  // of price (price_total=0 on backfilled blocking bookings
                  // is meaningless for the label).
                  <span>{activityLabel.organizerFirstName || catStyle.label}</span>
                ) : info.booking.standard_price_total &&
                info.booking.standard_price_total !== info.booking.price_total ? (
                  <>
                    <span className="line-through mr-1">
                      {info.booking.standard_price_total} kr
                    </span>
                    <span className="font-medium">
                      {info.booking.price_total ?? 0} kr
                    </span>
                  </>
                ) : (
                  <span>
                    {info.booking.price_total != null
                      ? `${info.booking.price_total} kr`
                      : catStyle.label}
                  </span>
                )}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs space-y-1 max-w-[220px]">
            <p className="font-semibold">
              {leagueMatchLabel
                ? leagueMatchLabel.teams
                : activityLabel
                  ? activityLabel.title
                  : playerName}
            </p>
            {leagueMatchLabel && (
              <p className="text-[10px] text-muted-foreground">
                Liga: {leagueMatchLabel.leagueName}
              </p>
            )}
            {!leagueMatchLabel && activityLabel && activityLabel.organizerFirstName && (
              <p className="text-[10px] text-muted-foreground">
                Organisatör: {activityLabel.organizerFirstName}
              </p>
            )}
            {(() => {
              const uc = catByUser.get(info.booking.user_id);
              return uc && uc.slug !== "standard" ? (
                <p>
                  👤{" "}
                  <Badge
                    variant="secondary"
                    className="text-[9px] px-1 py-0"
                  >
                    {uc.name}
                  </Badge>
                </p>
              ) : null;
            })()}
            <p>📞 {phone}</p>
            <p>
              🕐 {format(bStart, "HH:mm")}–{format(bEnd, "HH:mm")} ·{" "}
              {info.booking.duration_minutes} min
            </p>
            <p>🏟️ {courtName}</p>
            {info.booking.standard_price_total &&
            info.booking.standard_price_total !== info.booking.price_total ? (
              <p>
                💰{" "}
                <span className="line-through">
                  {info.booking.standard_price_total} kr
                </span>{" "}
                →{" "}
                <span className="font-semibold">
                  {info.booking.price_total ?? 0} kr
                </span>
              </p>
            ) : (
              <p>💰 {price}</p>
            )}
            <p>🔑 PIN: {pinCode}</p>
            {hallTyped?.access_code && <p>🚪 Kod: {hallTyped.access_code}</p>}
            <p>📋 {catStyle.label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // PR-D Layer 1 (TASK-75) — League match overlay.
  // Renders for placed league_matches (booking_id IS NULL). Spans
  // multiple cells when match_duration_minutes > 30; mid-cells return
  // null (mirrors training/booking span behaviour).
  const lmInfo = getLeagueMatchSlotInfo(courtId, slot);
  if (lmInfo && !lmInfo.isFirst) return null;
  if (lmInfo && lmInfo.isFirst) {
    const startTimeStr = lmInfo.match.scheduled_time?.slice(0, 5) ?? "";
    const duration = lmInfo.match.leagues?.match_duration_minutes ?? 90;
    const leagueName = lmInfo.match.leagues?.name ?? "";
    const tooltip = `${t("leagues.calendar.leagueMatchTooltip")}${leagueName ? ` · ${leagueName}` : ""} · ${startTimeStr} (${duration} min)`;
    // Tidsbokning Phase A — Seriespel block is now clickable.
    // Click opens MatchDetailModal (read-only in Phase A, edit-capable in Phase C).
    // We render as <button> when onMatchClick is provided, plain div otherwise.
    const clickableProps = onMatchClick
      ? {
          role: "button" as const,
          tabIndex: 0,
          onClick: () => {
            // If we just released a drag, suppress the click (drag flow opens
            // the dialog with pre-fill, click flow opens read-only modal).
            if (!matchDrag) onMatchClick(lmInfo.match.id);
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onMatchClick(lmInfo.match.id);
            }
          },
        }
      : {};
    const clickableClass = onMatchClick
      ? " cursor-pointer hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
      : "";
    // Tidsbokning Phase D — pointer-event drag handler. Mirror of the
    // booking drag pattern in lines ~141-160 above. AdminScheduleGrid's
    // global pointermove activates matchDrag state after MIN_DRAG_DISTANCE.
    const dragProps = onMatchDragStart
      ? {
          onPointerDown: (e: React.PointerEvent) =>
            onMatchDragStart(
              lmInfo.match.id,
              courtIdx,
              slotIdx,
              lmInfo.slotCount,
              lmInfo.match.leagues?.match_duration_minutes ?? 90,
              e,
            ),
        }
      : {};
    // Dim the source block while it's being dragged so the user has visual
    // feedback. Mirrors the booking-drag opacity reduction.
    const isMatchDragSource =
      !!matchDrag &&
      matchDrag.matchId === lmInfo.match.id &&
      matchDrag.originCourtIdx === courtIdx &&
      matchDrag.originSlotIdx === slotIdx;
    const dragClass = isMatchDragSource ? " opacity-40" : "";
    return (
      <div
        key={slot.toISOString()}
        className={"rounded-sm overflow-hidden z-10 flex flex-col justify-center px-1.5 border min-w-0 h-full bg-purple-200/40 dark:bg-purple-900/20 border-purple-400/60 dark:border-purple-700/60" + clickableClass + dragClass}
        style={{ gridColumn: `${gridCol} / span ${lmInfo.slotCount}` }}
        title={tooltip}
        {...clickableProps}
        {...dragProps}
      >
        <div className="truncate text-[10px] leading-tight text-left font-semibold text-purple-700 dark:text-purple-300">
          {t("leagues.calendar.leagueMatchSlot")}
        </div>
        <div className="truncate text-[9px] leading-tight text-left text-purple-700/70 dark:text-purple-300/70">
          {startTimeStr} ({duration} min)
        </div>
      </div>
    );
  }

  // Training session
  const tsInfo = getTrainingSlotInfo(courtId, slot);
  if (tsInfo && !tsInfo.isFirst) return null;
  if (tsInfo && tsInfo.isFirst) {
    const ts = tsInfo.training as ScheduleTrainingSession;
    const color = ts.training_groups?.color || "hsl(var(--primary))";
    return (
      <div
        key={slot.toISOString()}
        className="rounded-sm overflow-hidden z-10 flex flex-col justify-center px-1.5 border-2 border-dashed min-w-0 h-full"
        style={{
          gridColumn: `${gridCol} / span ${tsInfo.slotCount}`,
          backgroundColor: `${color}20`,
          borderColor: `${color}80`,
        }}
      >
        <div className="truncate text-[10px] leading-tight text-left">
          <span className="font-medium">
            {ts.training_groups?.name || t("scheduleCell.training")}
          </span>
        </div>
        <div className="truncate text-[9px] text-muted-foreground leading-tight text-left">
          {ts.start_time?.slice(0, 5)} ({ts.duration_minutes}min)
        </div>
      </div>
    );
  }

  // Named recurring block
  const recurringBlock = getRecurringBlockAt(courtId, slot);
  if (recurringBlock) {
    const blockColor = recurringBlock.color || "#64748B";
    const blockStartTime = recurringBlock.start_time?.slice(0, 5) ?? "";
    const blockEndTime = recurringBlock.end_time?.slice(0, 5) ?? "";
    const slotTime = format(slot, "HH:mm");
    const isFirst = slotTime === blockStartTime || slotTime + ":00" === recurringBlock.start_time;
    if (!isFirst) return null;
    // Calculate slot count
    const startParts = recurringBlock.start_time.split(":").map(Number);
    const endParts = recurringBlock.end_time.split(":").map(Number);
    const durationMin = (endParts[0] * 60 + endParts[1]) - (startParts[0] * 60 + startParts[1]);
    const blockSlotCount = Math.max(1, Math.ceil(durationMin / 30));
    return (
      <div
        key={slot.toISOString()}
        role="button"
        className="rounded-sm overflow-hidden z-10 flex flex-col justify-center px-1.5 border min-w-0 h-full cursor-pointer hover:opacity-80 transition-opacity"
        style={{
          gridColumn: `${gridCol} / span ${blockSlotCount}`,
          backgroundColor: `${blockColor}25`,
          borderColor: blockColor,
          backgroundImage: `repeating-linear-gradient(135deg, transparent, transparent 4px, ${blockColor}10 4px, ${blockColor}10 8px)`,
        }}
        title={`${recurringBlock.name} · ${blockStartTime}–${blockEndTime}${recurringBlock.price ? ` · ${recurringBlock.price} kr` : ""}`}
        onClick={() => onRecurringBlockClick(recurringBlock)}
      >
        <div className="truncate text-[10px] leading-tight text-left font-semibold" style={{ color: blockColor }}>
          {recurringBlock.name}
        </div>
        <div className="truncate text-[9px] text-muted-foreground leading-tight text-left">
          {blockStartTime}–{blockEndTime}
        </div>
      </div>
    );
  }

  // Recurring booking (subscription)
  const recurringBooking = getRecurringBookingAt(courtId, slot);
  if (recurringBooking) {
    const rbStartTime = recurringBooking.start_time?.slice(0, 5) ?? "";
    const rbEndTime = recurringBooking.end_time?.slice(0, 5) ?? "";
    const slotTime = format(slot, "HH:mm");
    const isFirst = slotTime === rbStartTime || slotTime + ":00" === recurringBooking.start_time;
    if (!isFirst) return null;
    const startParts = recurringBooking.start_time.split(":").map(Number);
    const endParts = recurringBooking.end_time.split(":").map(Number);
    const durationMin = (endParts[0] * 60 + endParts[1]) - (startParts[0] * 60 + startParts[1]);
    const rbSlotCount = Math.max(1, Math.ceil(durationMin / 30));
    const subColor = "#0891b2";
    const customerName = rbCustomerMap?.get(recurringBooking.customer_id) || t("scheduleCell.subscription");

    // Check if this subscription occurrence has been released
    const release = getSubscriptionReleaseAt?.(courtId, slot);
    const isReleased = release?.status === "released";
    const isRebooked = release?.status === "rebooked";

    // If rebooked, don't render subscription — the actual booking renders instead
    if (isRebooked) return null;

    // If released, show as available/bookable slot with dashed border
    if (isReleased) {
      return (
        <div
          key={slot.toISOString()}
          className="rounded-sm overflow-hidden z-10 flex flex-col justify-center px-1.5 min-w-0 h-full cursor-pointer hover:opacity-80 transition-opacity"
          style={{
            gridColumn: `${gridCol} / span ${rbSlotCount}`,
            backgroundColor: `${subColor}08`,
            border: `2px dashed ${subColor}60`,
          }}
          title={t("schedule.releasedSlot", { name: customerName, time: `${rbStartTime}–${rbEndTime}` })}
          // schedule-grid-gap-2-fix (Q-EX1=A): click the "Ledig" placeholder
          // opens SubscriptionDetailDialog for the underlying recurring_booking.
          // The dialog's useEffect detects the release row for the current
          // selectedDate and toggles the button to "Ångra ledigställning",
          // so admin can undo the release in-context without hunting for an
          // active date of the subscription.
          onClick={() => onSubscriptionClick(recurringBooking)}
        >
          <div className="truncate text-[10px] leading-tight text-left font-medium" style={{ color: `${subColor}90` }}>
            {t("schedule.available")}
          </div>
          <div className="truncate text-[9px] text-muted-foreground/60 leading-tight text-left">
            {rbStartTime}–{rbEndTime}
          </div>
        </div>
      );
    }

    return (
      <div
        key={slot.toISOString()}
        role="button"
        className="rounded-sm overflow-hidden z-10 flex flex-col justify-center px-1.5 border min-w-0 h-full cursor-pointer hover:opacity-80 transition-opacity"
        style={{
          gridColumn: `${gridCol} / span ${rbSlotCount}`,
          backgroundColor: `${subColor}20`,
          borderColor: subColor,
        }}
        title={`${customerName} · ${rbStartTime}–${rbEndTime}${recurringBooking.price ? ` · ${recurringBooking.price} kr` : ""}`}
        onClick={() => onSubscriptionClick(recurringBooking)}
      >
        <div className="truncate text-[10px] leading-tight text-left font-semibold" style={{ color: subColor }}>
          {customerName}
        </div>
        <div className="truncate text-[9px] text-muted-foreground leading-tight text-left">
          {rbStartTime}–{rbEndTime}{recurringBooking.comment ? ` · ${recurringBooking.comment}` : ""}
        </div>
      </div>
    );
  }

  // FIX #3: Empty slot — styled as interactive mintgreen cell
  const isFullHourBorder = slot.getMinutes() === 0;
  // UPPDRAG-31 Bug 2: paint the FULL drop range (slotCount cells starting
  // at targetSlotIdx) — not just the cursor cell. Green if validateDropZone
  // approved this drop, red if it would reject (out of bounds or
  // occupied). The validity flag lives on bookingDrag.dropValid and is
  // refreshed on every pointermove in AdminScheduleGrid.
  const isInDropRange =
    !!bookingDrag &&
    bookingDrag.targetCourtIdx === courtIdx &&
    slotIdx >= bookingDrag.targetSlotIdx &&
    slotIdx < bookingDrag.targetSlotIdx + bookingDrag.slotCount;
  // Tidsbokning Phase D — parallel drop-zone painting for matchDrag. Same
  // green/red semantics as bookingDrag's preview.
  const isInMatchDropRange =
    !!matchDrag &&
    matchDrag.targetCourtIdx === courtIdx &&
    slotIdx >= matchDrag.targetSlotIdx &&
    slotIdx < matchDrag.targetSlotIdx + matchDrag.slotCount;
  const dropZoneClass = isInDropRange
    ? bookingDrag?.dropValid === false
      ? "bg-red-200/60 ring-1 ring-red-400"
      : "bg-green-200/60 ring-1 ring-green-400"
    : isInMatchDropRange
      ? matchDrag?.dropValid === false
        ? "bg-red-200/60 ring-1 ring-red-400"
        : "bg-green-200/60 ring-1 ring-green-400"
      : null;
  return (
    <button
      key={slot.toISOString()}
      onMouseDown={(e) => {
        // Fas 2 click-to-move UX-cleanup: when move-mode is active, suppress
        // drag-start so no selection-rect is created. Cell-onClick still fires
        // and routes via handleSlotClick → move-branch (Fas 0.5 verified).
        if (clickToMoveBooking) return;
        if (!bookingDrag) { e.preventDefault(); onDragStart(courtIdx, slotIdx); }
      }}
      onMouseEnter={() => { if (!bookingDrag) onDragEnter(courtIdx, slotIdx); }}
      onClick={() => {
        if (!dragRef.current && !bookingDrag) onSlotClick(courtIdx, slotIdx);
      }}
      className={cn(
        "h-full transition-colors",
        isFullHourBorder
          ? "border-l border-border"
          : "border-l border-border/20",
        dropZoneClass
          ? dropZoneClass
          : selected
            ? "bg-primary/30 ring-1 ring-primary/40"
            : "bg-court-free/5 hover:bg-court-free/20 cursor-pointer border-b-0",
      )}
      style={{ gridColumn: gridCol }}
      title={format(slot, "HH:mm")}
    />
  );
}
